import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { adminAuth, setAuthToken } from "@/lib/api";
import { setAdminAuthenticated } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { mascotBase64 } from "@/assets/mascotBase64";
import { ShieldAlert, KeyRound, ChevronDown, ChevronUp, ShieldCheck, Lock } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleGoogleSuccess = useCallback(async (response) => {
    setLoading(true);
    try {
      const res = await api.post("/admin/google-auth", {
        id_token: response.credential
      });
      if (res.data.token) {
        setAuthToken(res.data.token);
        setAdminAuthenticated(true);
        const adminUser = {
          id: res.data.user_id || "admin",
          username: res.data.name || res.data.email || "Tech",
          role: "admin",
          email: res.data.email
        };
        localStorage.setItem("hm_user", JSON.stringify(adminUser));
        if (res.data.email) {
          localStorage.setItem("hm_admin_email", res.data.email);
        }
        toast.success(`Welcome, ${res.data.name || res.data.email}!`);
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Google login error:", error);
      const msg = error.response?.data?.detail || "Google authentication failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!clientId) return;

    // Load Google Identity Services script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleSuccess,
        });
        const btnContainer = document.getElementById("google-signin-btn");
        if (btnContainer) {
          window.google.accounts.id.renderButton(btnContainer, {
            theme: "outline",
            size: "large",
            type: "standard",
            shape: "pill",
            text: "signin_with",
            logo_alignment: "left",
            width: 300,
          });
        }
      }
    };
    document.body.appendChild(script);

    return () => {
      try {
        document.body.removeChild(script);
      } catch (e) {}
    };
  }, [clientId, handleGoogleSuccess]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Please enter the password");
      return;
    }

    setLoading(true);
    try {
      const result = await adminAuth(password);
      setAdminAuthenticated(true);
      if (result.token) {
        setAuthToken(result.token);
      }
      const adminUser = {
        id: "admin",
        username: "Tech",
        role: "admin"
      };
      localStorage.setItem("hm_user", JSON.stringify(adminUser));
      toast.success("Welcome to Admin Portal");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid password or connection error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1320] flex items-center justify-center p-6 relative overflow-hidden text-slate-100" data-testid="admin-login">
      {/* Subtle Ambient Radial Glows */}
      <div className="absolute -top-40 right-1/4 w-[450px] h-[450px] bg-emerald-500/10 rounded-full blur-[140px] pointer-events-none"></div>
      <div className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[140px] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-20 pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Brand Header */}
        <div className="text-center mb-8">
          {/* Large Logo */}
          <div className="flex items-center justify-center mb-6">
            <img 
              src={mascotBase64} 
              alt="Hidden Monkey Stays" 
              className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl"
            />
          </div>

          <div className="flex items-center justify-center gap-1.5 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-semibold tracking-widest uppercase">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Koko Operations Console
            </span>
          </div>

          <h1 className="font-bold text-2xl md:text-3xl text-white tracking-tight">
            Koko by Hidden Monkey
          </h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1.5 tracking-wide">
            Guest experience & multi-property operations
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-[#111C2D]/85 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-7 md:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)]">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-white">
              Administrator Sign In
            </h2>
            <p className="text-slate-400 text-xs mt-1">
              Verify your identity to access property management controls
            </p>
          </div>

          {/* Primary: Google Sign In */}
          <div className="flex flex-col items-center justify-center my-4 space-y-3">
            <div id="google-signin-btn" className="flex justify-center min-h-[44px]"></div>
            
            {!clientId && (
              <div className="w-full bg-amber-500/10 border border-amber-500/20 text-amber-200 rounded-xl p-3.5 text-xs">
                <p className="font-semibold mb-1 flex items-center gap-1.5 text-amber-300">
                  <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0" /> Google OAuth Setup
                </p>
                <p className="text-amber-200/80 leading-relaxed text-[11px]">
                  Provide your <code className="bg-amber-500/20 px-1 py-0.5 rounded text-amber-200">REACT_APP_GOOGLE_CLIENT_ID</code> to activate one-click Google Sign-In. You can sign in using master password below in the meantime.
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/[0.08]"></div>
            </div>
            <div className="relative flex justify-center text-[11px] uppercase">
              <span className="bg-[#111C2D] px-3 text-slate-400 font-medium tracking-wider">
                or sign in with password
              </span>
            </div>
          </div>

          {/* Emergency Fallback Accordion */}
          <div>
            <button
              type="button"
              onClick={() => setShowPasswordFallback(!showPasswordFallback)}
              className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-200 py-2 px-3 rounded-lg hover:bg-white/[0.03] border border-white/[0.05] transition-all"
            >
              <span className="flex items-center gap-2 font-medium">
                <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                {clientId ? "Emergency Master Password Login" : "Master Password Login"}
              </span>
              {showPasswordFallback || !clientId ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>

            {(showPasswordFallback || !clientId) && (
              <form onSubmit={handlePasswordLogin} className="space-y-3.5 mt-3.5 animate-fade-in" data-testid="login-form">
                <div>
                  <Label htmlFor="password" className="text-xs text-slate-300 font-medium">Master Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 text-sm h-11 bg-[#0A101D] border-white/10 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 rounded-xl"
                    placeholder="Enter master password"
                    data-testid="login-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-sm font-semibold rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-950/40 transition-all"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? "Signing in..." : "Sign In with Password"}
                </Button>
              </form>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-white/[0.06]">
            <div className="text-center">
              <Link 
                to="/forgot-password?return=/admin&username=admin" 
                className="text-slate-400 hover:text-emerald-400 text-xs transition-colors font-medium"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>
          </div>
        </div>
        
        {/* Security Assurance Footer */}
        <div className="mt-6 text-center space-y-2">
          <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <Lock className="w-3 h-3 text-emerald-500/70" />
            <span>256-bit Encrypted Session • Authorized Personnel Only</span>
          </div>
          <p className="text-slate-600 text-[11px]">
            www.hiddenmonkeystays.com
          </p>
        </div>
      </div>
    </div>
  );
}
